import { XeroApexInvoiceSimple, getXeroApexInvoices } from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../hooks/useAbortableEffect';

type UseAdminInvoicesResult = {
    invoices: XeroApexInvoiceSimple[];
    isLoading: boolean;
    hasErrorLoadingData: boolean;
    initiated: boolean;
    initiate: () => void;
    updateInvoiceData: (invoice: XeroApexInvoiceSimple) => void;
};

export function useAdminInvoices(invoiceIds: number[]): UseAdminInvoicesResult {
    const [invoices, setInvoices] = useState<XeroApexInvoiceSimple[]>([]);
    const [initiated, setIsInitiated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoadingData, setHasErrorLoadingData] = useState(false);

    const fetchInvoices = useCallback(
        async (invoiceIds: number[], requestInit: RequestInit) => {
            const filteredInvoiceIds = invoiceIds.filter((id) => !invoices.find((i) => i.id === id));
            if (filteredInvoiceIds.length === 0) {
                return;
            }

            try {
                setIsLoading(true);
                setHasErrorLoadingData(false);
                const invoicesResponse = await getXeroApexInvoices(
                    { id: filteredInvoiceIds.join(','), page_size: filteredInvoiceIds.length },
                    requestInit
                );
                if (invoicesResponse.status === 200) {
                    setInvoices((prev) => prev.concat(invoicesResponse.data.results));
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoadingData(true);
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [invoices]
    );

    useAbortableEffect(
        (signal) => {
            if (!initiated) {
                return;
            }
            fetchInvoices(invoiceIds, { signal });
        },
        [fetchInvoices, invoiceIds, initiated]
    );

    const initiate = useCallback(() => {
        setIsInitiated(true);
    }, []);

    const updateInvoiceData = useCallback((invoice: XeroApexInvoiceSimple) => {
        setInvoices((invoices) => {
            if (!invoices.find((i) => i.id === invoice.id)) {
                return invoices.concat(invoice);
            } else {
                return invoices;
            }
        });
    }, []);

    return {
        invoices,
        isLoading,
        hasErrorLoadingData,
        initiated,
        updateInvoiceData,
        initiate,
    };
}
